import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { staffPageUrl, qrEmbedOrigin } from "@/lib/appOrigins";
import { staffIssueToken } from "@/lib/api";

const STAFF_STORAGE = "baoobao_staff_key";
const DEFAULT_STAFF_KEY = "dev-staff-key-change-me";

const ENV_STAFF = (import.meta.env.VITE_STAFF_KEY ?? "").trim();

export default function StaffPage() {
  const [staffKey, setStaffKey] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_STAFF_KEY;
    if (ENV_STAFF) return ENV_STAFF;
    return localStorage.getItem(STAFF_STORAGE) || DEFAULT_STAFF_KEY;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{
    qrDataUrl: string;
    expiresAt: number;
    token: string;
    qrString: string;
  } | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (ENV_STAFF) return;
    localStorage.setItem(STAFF_STORAGE, staffKey);
  }, [staffKey]);

  const issue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const origin = qrEmbedOrigin();
      const res = await staffIssueToken(staffKey, {
        embedUrl: Boolean(origin),
        publicOrigin: origin || undefined,
        qrWidth: 480,
      });
      setIssued({
        qrDataUrl: res.qrDataUrl,
        expiresAt: Date.now() + res.expiresInMs,
        token: res.token,
        qrString: res.qrString,
      });
      setFullscreen(false);
    } catch (e) {
      setIssued(null);
      setError(e instanceof Error ? e.message : "発行に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [staffKey]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!issued) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [issued]);

  const remainingSec = issued ? Math.max(0, Math.ceil((issued.expiresAt - now) / 1000)) : 0;

  const qrBlock = issued && (
    <div
      className="rounded-2xl p-4 sm:p-6 text-center space-y-4"
      style={{
        background: "linear-gradient(135deg, #1a2f2f 0%, #0d1f1f 100%)",
        border: "1px solid rgba(212,175,55,0.35)",
      }}
    >
      <p className="text-xs" style={{ color: "rgba(244,229,195,0.6)" }}>
        残り{" "}
        <span className="font-bold tabular-nums text-lg" style={{ color: "#d4af37" }}>
          {remainingSec}
        </span>{" "}
        秒で無効（1回スキャンで消費）
      </p>
      <div className="flex justify-center">
        <div className="bg-white rounded-xl p-3 sm:p-4 shadow-lg">
          <img src={issued.qrDataUrl} alt="会計用ワンタイムQR" className="w-72 h-72 sm:w-80 sm:h-80 object-contain" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="px-4 py-2 rounded-xl text-xs font-bold"
          style={{
            background: "rgba(212,175,55,0.2)",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#f4e5c3",
          }}
        >
          全画面表示
        </button>
        <button
          type="button"
          onClick={() => void issue()}
          disabled={loading}
          className="px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-50"
          style={{
            background: "linear-gradient(135deg, #b8860b 0%, #d4af37 100%)",
            color: "#1a1a2e",
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          新しいQRを発行
        </button>
      </div>
      <button
        type="button"
        onClick={() => setShowToken((v) => !v)}
        className="text-[10px] underline"
        style={{ color: "rgba(244,229,195,0.35)" }}
      >
        {showToken ? "トークンを隠す" : "トークンを表示（サポート用）"}
      </button>
      {showToken && (
        <p className="text-[10px] break-all font-mono text-left px-1" style={{ color: "rgba(244,229,195,0.45)" }}>
          {issued.qrString}
        </p>
      )}
    </div>
  );

  return (
    <div
      className="min-h-screen px-4 py-8"
      style={{
        background: "linear-gradient(160deg, #0d1f1f 0%, #1a2f2f 40%, #0f1a1a 100%)",
        fontFamily: "Noto Sans JP, sans-serif",
      }}
    >
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg font-bold" style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}>
            店舗・会計用 QR
          </h1>
          <Link to="/login" className="text-xs underline shrink-0" style={{ color: "rgba(212,175,55,0.7)" }}>
            会員ログインへ
          </Link>
        </div>

        <p className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.55)" }}>
          「QRを発行」→ 画面をお客様に向ける → お客様はアプリの「QR読取」でスキャン。コードは<strong>先着1名・1回限り</strong>で、時間が経つと無効になります。
        </p>

        <div
          className="rounded-xl px-3 py-2 space-y-1"
          style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)" }}
        >
          <p className="text-[10px]" style={{ color: "rgba(244,229,195,0.45)" }}>
            店舗タブレット用（ブックマーク）
          </p>
          <p className="text-[11px] break-all font-mono leading-snug" style={{ color: "rgba(212,175,55,0.85)" }}>
            {staffPageUrl()}
          </p>
        </div>

        <div>
          <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
            スタッフキー（サーバーの STAFF_API_KEY と一致）
            {ENV_STAFF ? (
              <span className="ml-1" style={{ color: "rgba(212,175,55,0.6)" }}>
                （VITE_STAFF_KEY でプリセット）
              </span>
            ) : null}
          </label>
          <input
            type="password"
            value={staffKey}
            onChange={(e) => setStaffKey(e.target.value)}
            readOnly={Boolean(ENV_STAFF)}
            className="w-full rounded-xl px-4 py-3 text-base outline-none font-mono disabled:opacity-80"
            style={{
              background: "rgba(212,175,55,0.08)",
              border: "1px solid rgba(212,175,55,0.25)",
              color: "#f4e5c3",
            }}
          />
        </div>

        <button
          type="button"
          onClick={() => void issue()}
          disabled={loading}
          className="w-full py-4 rounded-2xl text-sm font-bold tracking-wider disabled:opacity-50"
          style={{
            background: "linear-gradient(135deg, #b8860b 0%, #d4af37 50%, #b8860b 100%)",
            color: "#1a1a2e",
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          {loading ? "発行中…" : issued ? "同じキーでもう一度発行" : "ワンタイムQRを発行"}
        </button>

        {error && (
          <p className="text-sm text-center" style={{ color: "#ff8080" }}>
            {error}
          </p>
        )}

        {qrBlock}
      </div>

      {fullscreen && issued && (
        <button
          type="button"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-6 border-0 cursor-pointer"
          style={{ background: "#0a0a0a" }}
          onClick={() => setFullscreen(false)}
          aria-label="全画面を閉じる"
        >
          <p className="text-xs mb-4" style={{ color: "rgba(212,175,55,0.8)" }}>
            タップで閉じる · 残り {remainingSec} 秒
          </p>
          <div className="bg-white p-4 rounded-2xl max-w-[min(90vw,420px)]">
            <img src={issued.qrDataUrl} alt="" className="w-full h-auto object-contain" />
          </div>
        </button>
      )}
    </div>
  );
}
